<script setup lang="ts">
import { ArrowTopRightOnSquareIcon, PlusCircleIcon, TrashIcon } from '@heroicons/vue/24/outline'
import { storeToRefs } from 'pinia'
import { useField, useFieldArray, useFormContext } from 'vee-validate'
import { onMounted, ref, watch } from 'vue'

import type { TWOrganizationAIResponse } from '@/types/external/2work/doctypes/generatorOutput/ERP2Work/TWOrganizationAIResponse'
import type { TWWeblinksDynamic } from '@/types/external/2work/doctypes/generatorOutput/ERP2Work/TWWeblinksDynamic'

import { TEST_IDS_CREATE } from '@/_testIds/testIdsCreate'
import CollapsableContent from '@/components/common/layout/CollapsableContent.vue'
import DivideContent from '@/components/common/layout/DivideContent.vue'
import ButtonTertiary from '@/components/common/ui/buttons/ButtonTertiary.vue'
import FormSelect from '@/components/common/ui/inputs/FormSelect.vue'
import InputGrid from '@/components/common/ui/inputs/InputGrid.vue'
import TextInput from '@/components/common/ui/inputs/TextInput.vue'
import ThreedotActionsOnHover from '@/components/common/ui/inputs/ThreedotActionsOnHover.vue'
import ContextMenuItem from '@/components/common/ui/overlays/ContextMenuItem.vue'
import { useBackendConstantsStore } from '@/stores/backendConstantsStore'
import {
  findOptionValueFromAiText,
  hasMatchingAiWeblink,
  normalizeAiWeblinks,
} from '@/utilities/organizationAi'
import { translationFunctionDefault } from '@/utilities/translations'

interface SectionWeblinksProps {
  addWeblinksDisabled?: boolean | undefined
  aiData?: null | TWOrganizationAIResponse | undefined
  inCreateContext?: boolean
  onSubmit?: (() => void) | undefined
}

const props = defineProps<SectionWeblinksProps>()

const tfDefault = translationFunctionDefault()

const backendConstantsStore = useBackendConstantsStore()
const { fetchWeblinkList } = backendConstantsStore
const { weblinkOptionList } = storeToRefs(backendConstantsStore)

const { setFieldValue } = useFormContext()
const weblinkFieldArray = useFieldArray<TWWeblinksDynamic>('tw_weblinks')

const aiFilledFields = ref<Set<string>>(new Set())

const getAiFieldCount = (): number => {
  return aiFilledFields.value.size
}

const acceptAIFields = (): void => {
  aiFilledFields.value.clear()
}

const isAiFieldsStillVisible = (): boolean => {
  return getAiFieldCount() > 0
}

const handleAddWeblink = (): void => {
  weblinkFieldArray.push({
    doctype: 'TW Weblinks',
    type: '',
    url: '',
  })
}

const handleRemoveWeblink = (weblinkItemIndex: number, submit = true): void => {
  const aiPathsToRemove = ['type', 'url']

  aiPathsToRemove.forEach((fieldName) => {
    const fullPath = `tw_weblinks.${weblinkItemIndex}.${fieldName}`
    aiFilledFields.value.delete(fullPath)
  })

  for (
    let weblinkIndex = weblinkItemIndex + 1;
    weblinkIndex < weblinkFieldArray.fields.value.length;
    weblinkIndex++
  ) {
    aiPathsToRemove.forEach((fieldName) => {
      const oldPath = `tw_weblinks.${weblinkIndex}.${fieldName}`
      const newPath = `tw_weblinks.${weblinkIndex - 1}.${fieldName}`

      if (aiFilledFields.value.has(oldPath)) {
        aiFilledFields.value.delete(oldPath)
        aiFilledFields.value.add(newPath)
      }
    })
  }

  weblinkFieldArray.remove(weblinkItemIndex)

  if (submit) props.onSubmit?.()
}

const clearAIFields = (): void => {
  for (
    let weblinkIndex = weblinkFieldArray.fields.value.length - 1;
    weblinkIndex >= 0;
    weblinkIndex--
  ) {
    const basePath = `tw_weblinks.${weblinkIndex}`
    const hasAiFields = ['type', 'url'].some((fieldName) =>
      aiFilledFields.value.has(`${basePath}.${fieldName}`),
    )

    if (hasAiFields) {
      handleRemoveWeblink(weblinkIndex, false)
    }
  }
}

const weblinkExistsAndFilled = (index: number): boolean => {
  const link = weblinkFieldArray.fields.value[index]?.value
  if (!link) return false

  return !!(link.type ?? link.url)
}

const getWeblinkTypeIdFromLabel = (label: string | undefined): string | undefined => {
  return findOptionValueFromAiText(label, weblinkOptionList.value)
}

const fillFields = (): void => {
  const aiWeblinks = normalizeAiWeblinks(props.aiData?.weblinks)

  aiWeblinks.forEach(({ type, url }) => {
    const existingUrls = weblinkFieldArray.fields.value.map((weblink) => weblink.value.url)
    if (hasMatchingAiWeblink(existingUrls, url)) return

    const targetIndex = weblinkFieldArray.fields.value.length

    weblinkFieldArray.push({
      doctype: 'TW Weblinks',
      type: '',
      url: '',
    })

    if (!weblinkExistsAndFilled(targetIndex)) {
      const weblinkTypeId = getWeblinkTypeIdFromLabel(type ?? undefined)

      const fields = [
        { path: `tw_weblinks.${targetIndex}.type`, value: weblinkTypeId ?? '' },
        { path: `tw_weblinks.${targetIndex}.url`, value: url || '' },
      ]

      fields.forEach(({ path, value }) => {
        const { value: fieldValue } = useField(path)
        if (!fieldValue.value && value) {
          setFieldValue(path, value)
          aiFilledFields.value.add(path)
        }
      })
    }
  })
}

onMounted(async () => {
  await fetchWeblinkList()
  fillFields()
})

watch(
  () => props.aiData,
  (newVal) => {
    if (newVal) {
      clearAIFields()
      fillFields()
    }
  },
)

defineExpose({
  acceptAIFields,
  clearAIFields,
  getAiFieldCount,
  isAiFieldsStillVisible,
})
</script>

<template>
  <CollapsableContent :title="tfDefault('editRelationGroup.weblinksSectionTitle')">
    <ThreedotActionsOnHover
      v-for="(currentWeblink, currentWeblinkIndex) in weblinkFieldArray.fields.value"
      :key="currentWeblink.key"
      menu-align="right"
      visibility="hover"
    >
      <InputGrid :columns="2" :data-testid="TEST_IDS_CREATE.sectionWeblinks.inputGrid">
        <FormSelect
          :data-testid="TEST_IDS_CREATE.sectionWeblinks.typeSelect"
          :in-create-context="inCreateContext ?? false"
          :is-filled-by-ai="aiFilledFields.has(`tw_weblinks.${currentWeblinkIndex}.type`)"
          :label="tfDefault('editRelationGroup.weblinkUrlTypeLabel')"
          :name="`tw_weblinks.${currentWeblinkIndex}.type`"
          on-white-bg
          :options="[weblinkOptionList]"
          searchable
          @blur="onSubmit"
          @manual-input="() => aiFilledFields.delete(`tw_weblinks.${currentWeblinkIndex}.type`)"
        />

        <TextInput
          :capitalize-first="false"
          :data-testid="TEST_IDS_CREATE.sectionWeblinks.urlInput"
          :in-create-context="inCreateContext ?? false"
          :is-filled-by-ai="aiFilledFields.has(`tw_weblinks.${currentWeblinkIndex}.url`)"
          :label="tfDefault('editRelationGroup.weblinkLabel')"
          :name="`tw_weblinks.${currentWeblinkIndex}.url`"
          on-white-bg
          type="url"
          @blur="onSubmit"
          @manual-input="() => aiFilledFields.delete(`tw_weblinks.${currentWeblinkIndex}.url`)"
        />
      </InputGrid>
      <template #actions>
        <a
          :href="currentWeblink.value.url ? `${currentWeblink.value.url}` : ''"
          rel="noopener noreferrer"
          target="_blank"
        >
          <ContextMenuItem
            :icon="ArrowTopRightOnSquareIcon"
            :text="tfDefault('editRelationGroup.gotoWebLink')"
          />
        </a>
        <DivideContent />
        <ContextMenuItem
          :icon="TrashIcon"
          :text="tfDefault('editRelationGroup.remove')"
          @click="() => handleRemoveWeblink(currentWeblinkIndex)"
        />
      </template>
    </ThreedotActionsOnHover>
    <ButtonTertiary
      :data-testid="TEST_IDS_CREATE.sectionWeblinks.addButton"
      :disabled="!!addWeblinksDisabled"
      :label="tfDefault('editRelationGroup.addWeblinkButtonLabel')"
      @click="handleAddWeblink"
    >
      <PlusCircleIcon class="mr-2 h-6 w-6" />
    </ButtonTertiary>
  </CollapsableContent>
</template>
